import createDOMPurify, { type DOMPurify } from "dompurify";

/**
 * The browser's second check of an SVG artefact (design §4): the server validated it against an
 * allowlist; DOMPurify's SVG profile, with the server's refusals, cleans it again here. The result
 * is only ever shown as an image of a `data:` URL, which runs no script and loads nothing,
 * whatever it holds; it is never inserted into the document. Opened on its own ("Open image in
 * new tab"), a `data:` document has an opaque origin: unlike a `blob:` URL, it never runs as ESI.
 */

// The server's refusals (packages/core artefact-svg.ts), as elements DOMPurify must drop.
const FORBID_TAGS = [
  "script",
  "foreignObject",
  "iframe",
  "image",
  "feImage",
  "animate",
  "animateMotion",
  "animateTransform",
  "animateColor",
  "set",
  "a",
  "discard",
  "handler",
  "listener",
];

/** `url(#id)` is the only reference kept; any other `url(` (or an escape) is refused. */
const FOREIGN_URL = /url\s*\(\s*(?!['"]?#)|\\|@import|expression\s*\(|-moz-binding|behavior\s*:/i;

const purifiers = new WeakMap<Window, DOMPurify>();

function instance(win: Window): DOMPurify {
  const known = purifiers.get(win);
  if (known) {
    return known;
  }
  // Its own instance, so these hooks never apply to other sanitising in the app.
  const p = createDOMPurify(win as Window & typeof globalThis);
  p.addHook("uponSanitizeAttribute", (_node, data) => {
    const name = data.attrName.toLowerCase();
    const value = data.attrValue;
    if (name.startsWith("on")) {
      data.keepAttr = false;
    } else if ((name === "href" || name === "xlink:href") && !value.trim().startsWith("#")) {
      data.keepAttr = false;
    } else if (FOREIGN_URL.test(value)) {
      data.keepAttr = false;
    }
  });
  p.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName === "style" && FOREIGN_URL.test(node.textContent ?? "")) {
      node.textContent = "";
    }
  });
  purifiers.set(win, p);
  return p;
}

/**
 * The sanitised SVG text, or null when nothing drawable is left. `win`: the window whose parser
 * DOMPurify uses (tests pass a jsdom window; happy-dom drops the SVG root).
 */
export function sanitizeSvg(svg: string, win: Window = window): string | null {
  const clean = instance(win).sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    // The server allows `use` and `symbol`; the hook above keeps local (`#id`) references only.
    ADD_TAGS: ["use", "symbol"],
    FORBID_TAGS,
    // Kept as SVG text: never a DOM node that could be adopted into the page.
    RETURN_TRUSTED_TYPE: false,
  });
  // HTML serialisation writes U+00A0 as `&nbsp;`, which XML (an image's parser) does not know:
  // the drawing would break. `&amp;nbsp;` (escaped text) never matches.
  const text = String(clean).replaceAll("&nbsp;", "&#160;");
  return /^\s*<svg[\s>]/i.test(text) ? withNamespaces(text) : null;
}

const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

/**
 * An image is parsed as XML, where an `<svg>` without the SVG namespace (fine inline, and accepted
 * by the server) draws nothing: the root's `xmlns` is set to SVG's, replacing any other (an XHTML
 * root would make it a document of another kind), and XLink's when `xlink:` is used.
 */
function withNamespaces(svg: string): string {
  const root = /^\s*<svg\b[^>]*>/i.exec(svg)?.[0] ?? "";
  let fixed = withAttribute(root, "xmlns", SVG_NS);
  if (/\bxlink:/.test(svg)) {
    fixed = withAttribute(fixed, "xmlns:xlink", XLINK_NS);
  }
  return fixed === root ? svg : fixed + svg.slice(root.length);
}

/** The root tag with `name` set to `value`: replaced when it holds anything else, else added. */
function withAttribute(root: string, name: string, value: string): string {
  const existing = new RegExp(`\\s${name}\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]*)`, "i");
  const set = ` ${name}="${value}"`;
  const found = existing.exec(root);
  if (found) {
    return found[0].replace(/^\s+/, " ") === set ? root : root.replace(existing, set);
  }
  return root.replace(/^(\s*<svg)\b/i, `$1${set}`);
}

/**
 * A `data:` URL of the sanitised SVG for an `<img>`, or null when nothing drawable is left.
 * Percent-encoded: a `#` in `url(#id)` would otherwise end the URL.
 */
export function svgDataUrl(svg: string, win: Window = window): string | null {
  const clean = sanitizeSvg(svg, win);
  return clean === null ? null : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(clean)}`;
}
